import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { FaTimes, FaChevronLeft, FaChevronRight, FaFilter, FaCalendarAlt, FaMapMarkerAlt, FaSearch } from 'react-icons/fa';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Detect mobile screen
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  // Gallery data with useMemo to avoid recreation on each render
  const galleryItems = useMemo(() => [
    {
      id: 1,
      title: 'Commercial Building Demolition',
      category: 'Demolition',
      description: 'Complete demolition of a commercial building in Crescent City, Florida. Our team safely dismantled the structure while recycling over 75% of the materials.',
      image: '/images/gallery/demo1.jpg',
      date: 'January 2023',
      location: 'Crescent City, FL',
      featured: true
    },
    {
      id: 2,
      title: 'Residential Land Clearing',
      category: 'Site Clearing',
      description: 'Land clearing and preparation for a new residential construction project. We removed vegetation, debris, and graded the land for optimal building conditions.',
      image: '/images/gallery/clearing1.jpg',
      date: 'February 2023',
      location: 'Palatka, FL',
      featured: false
    },
    {
      id: 3,
      title: 'Industrial Concrete Removal',
      category: 'Concrete Removal',
      description: 'Removal of concrete foundation from an industrial site. Precision work was required to avoid damage to adjacent structures and underground utilities.',
      image: '/images/gallery/concrete1.jpg',
      date: 'March 2023',
      location: 'Jacksonville, FL',
      featured: false
    },
    {
      id: 4,
      title: 'Residential Demolition',
      category: 'Demolition',
      description: 'Partial demolition of an outdated residential property for renovation. Our selective demolition approach preserved valuable structural elements while removing outdated sections.',
      image: '/images/gallery/demo2.jpg',
      date: 'April 2023',
      location: 'Putnam County, FL',
      featured: true
    },
    {
      id: 5,
      title: 'Tree Removal for Development',
      category: 'Tree Removal',
      description: 'Large-scale tree removal for a new housing development. Our specialized equipment allowed for quick and safe removal of numerous large trees.',
      image: '/images/gallery/tree1.jpg',
      date: 'May 2023',
      location: 'Crescent City, FL',
      featured: false
    },
    {
      id: 6,
      title: 'Office Building Interior Demolition',
      category: 'Interior Demolition',
      description: 'Interior demolition for office building renovation project. We carefully removed interior walls, fixtures, and finishes while maintaining the structural integrity of the building.',
      image: '/images/gallery/interior1.jpg',
      date: 'June 2023',
      location: 'St. Augustine, FL',
      featured: true
    },
    {
      id: 7,
      title: 'Residential Property Clearing',
      category: 'Site Clearing',
      description: 'Complete clearing of a residential property for new home construction. We removed all vegetation, rocks, and debris to prepare for foundation work.',
      image: '/images/services/realistic_scene_showing_a_skid_steer_loader.jpg',
      date: 'July 2023',
      location: 'Putnam County, FL',
      featured: false
    },
    {
      id: 8,
      title: 'Commercial Site Preparation',
      category: 'Site Clearing',
      description: 'Preparation of a large commercial property for development. Our services included vegetation removal, grading, and excavation.',
      image: '/images/services/A_realistic_construction_site_showing_a_skid_steer.jpg',
      date: 'August 2023', 
      location: 'Gainesville, FL',
      featured: false
    },
    {
      id: 9,
      title: 'Warehouse Interior Demolition',
      category: 'Interior Demolition',
      description: 'Interior demolition of an old warehouse for repurposing as a modern retail space. We safely removed interior structures while preserving the main building frame.',
      image: '/images/services/A_photorealistic_interior_demolition_scene_showin_.jpeg',
      date: 'September 2023',
      location: 'Palatka, FL',
      featured: true
    }
  ], []);
  
  // Get unique categories for filter
  const categories = useMemo(() => 
    ['all', ...new Set(galleryItems.map(item => item.category))], 
  [galleryItems]);
  
  // Featured projects
  const featuredProjects = useMemo(() => 
    galleryItems.filter(item => item.featured),
  [galleryItems]);
  
  // Filtered and searched items
  const filteredItems = useMemo(() => {
    // Start loading
    setIsLoading(true);
    
    // Filter items based on category and search term
    let result = galleryItems;
    
    // Filter by category
    if (activeFilter !== 'all') {
      result = result.filter(item => item.category === activeFilter);
    }
    
    // Filter by search term
    if (searchTerm.trim() !== '') {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(item => 
        item.title.toLowerCase().includes(search) || 
        item.description.toLowerCase().includes(search) || 
        item.location.toLowerCase().includes(search) ||
        item.category.toLowerCase().includes(search)
      );
    }
    
    // Simulate loading delay for better UX
    setTimeout(() => {
      setIsLoading(false);
    }, 400);
    
    return result;
  }, [galleryItems, activeFilter, searchTerm]);
  
  // Handle filter change
  const handleFilterChange = useCallback((category) => {
    setActiveFilter(category);
    // Reset search term when changing filters
    setSearchTerm('');
  }, []);
  
  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);
  
  // Lightbox functions
  const openLightbox = useCallback((image) => {
    const index = filteredItems.findIndex(item => item.id === image.id);
    setSelectedIndex(index);
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  }, [filteredItems]);
  
  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  }, []);
  
  const goToNext = useCallback((e) => {
    e && e.stopPropagation();
    const nextIndex = (selectedIndex + 1) % filteredItems.length;
    setSelectedIndex(nextIndex);
    setSelectedImage(filteredItems[nextIndex]);
  }, [selectedIndex, filteredItems]);
  
  const goToPrevious = useCallback((e) => {
    e && e.stopPropagation();
    const prevIndex = (selectedIndex - 1 + filteredItems.length) % filteredItems.length;
    setSelectedIndex(prevIndex);
    setSelectedImage(filteredItems[prevIndex]);
  }, [selectedIndex, filteredItems]);
  
  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      switch (e.key) {
        case 'ArrowRight':
          goToNext(e);
          break;
        case 'ArrowLeft':
          goToPrevious(e);
          break;
        case 'Escape':
          closeLightbox();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, goToNext, goToPrevious, closeLightbox]);
  
  // Simulate loading effect
  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <>
      <Helmet>
        <title>Project Gallery | Florida's Top Site Service & Demolition</title>
        <meta name="description" content="View our portfolio of demolition and site clearing projects. See examples of our quality work in Crescent City, Florida and surrounding areas." />
        <meta name="keywords" content="demolition projects, site clearing examples, construction demolition, concrete removal projects, Florida demolition gallery" />
        <link rel="canonical" href="https://www.floridastopsite.com/gallery" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Project Gallery | Florida's Top Site Service & Demolition" />
        <meta property="og:description" content="View our portfolio of demolition and site clearing projects. See examples of our quality work in Crescent City, Florida and surrounding areas." />
        <meta property="og:url" content="https://www.floridastopsite.com/gallery" />
        <meta property="og:image" content="https://www.floridastopsite.com/images/og-image.jpg" />
      </Helmet>
      
      <div style={{
        height: '70vh',
        minHeight: '500px',
        background: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(\'/images/hero-bg.jpg\') center/cover no-repeat',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
      }}>
        {/* Radial gradient overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at center, rgba(255, 140, 0, 0.2) 0%, rgba(0, 0, 0, 0) 70%)'
        }} />
        
        {/* Content */}
        <div style={{
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '10px',
          }}>
            <h1 style={{
              color: 'white',
              fontSize: window.innerWidth <= 768 ? '3.2rem' : '4.5rem',
              marginBottom: '1.5rem',
              textShadow: '0 2px 5px rgba(0, 0, 0, 0.5)'
            }}>
              Our Project Gallery
            </h1>
            
            <p style={{
              fontSize: window.innerWidth <= 768 ? '1.6rem' : '2rem',
              color: 'white',
              marginBottom: '2.5rem'
            }}>
              Showcasing exceptional demolition and site clearing work across Northeast Florida
            </p>
          </div>
        </div>
      </div>
      
      {featuredProjects.length > 0 && (
        <FeaturedSection>
          <div className="container">
            <div style={{
              textAlign: 'center',
              marginBottom: window.innerWidth <= 768 ? '30px' : '50px'
            }}>
              <h2 style={{
                fontSize: window.innerWidth <= 768 ? '28px' : '36px',
                fontWeight: '700',
                color: 'var(--text-color)',
                marginBottom: '15px',
                position: 'relative',
                display: 'inline-block'
              }}>
                Featured Projects
                <span style={{
                  content: '',
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60px',
                  height: '3px',
                  backgroundColor: '#ff8c00'
                }}></span>
              </h2>
              <p style={{
                fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                color: '#999',
                maxWidth: '600px',
                margin: '0 auto',
                marginTop: '20px'
              }}>
                Explore some of our most impressive demolition and site clearing work
              </p>
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth <= 768 
                ? 'repeat(auto-fill, minmax(320px, 1fr))' 
                : 'repeat(2, 1fr)',
              gap: window.innerWidth <= 768 ? '20px' : '30px',
              marginTop: '20px',
              maxWidth: '1200px',
              margin: '20px auto 0'
            }}>
              {featuredProjects.map((item) => (
                <div 
                  key={item.id}
                  style={{ 
                    cursor: 'pointer', 
                    background: 'rgba(10, 10, 10, 0.5)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.4)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: window.innerWidth <= 768 ? '380px' : '450px',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                  onClick={() => openLightbox(item)}
                  onMouseOver={(e) => {
                    if (window.innerWidth > 768) {
                      e.currentTarget.style.transform = 'translateY(-10px)';
                      e.currentTarget.style.boxShadow = '0 25px 50px rgba(255, 140, 0, 0.4)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (window.innerWidth > 768) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.4)';
                    }
                  }}
                  onTouchStart={(e) => {
                    e.currentTarget.style.transform = 'scale(0.98)';
                    e.currentTarget.style.transition = 'transform 0.2s ease';
                  }}
                  onTouchEnd={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.transition = 'transform 0.2s ease';
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      style={{ 
                        width: '100%', 
                        height: window.innerWidth <= 768 ? '230px' : '280px', 
                        objectFit: 'cover',
                        display: 'block'
                      }} 
                    />
                    <div style={{ 
                      position: 'absolute', 
                      top: '12px', 
                      right: '12px',
                      background: '#ff8c00',
                      color: 'white',
                      padding: '6px 16px',
                      borderRadius: '30px',
                      fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.4)'
                    }}>
                      {item.category}
                    </div>
                  </div>
                  
                  <div style={{ padding: window.innerWidth <= 768 ? '18px' : '25px', flexGrow: 1 }}>
                    <h3 style={{ 
                      color: 'white', 
                      fontSize: window.innerWidth <= 768 ? '22px' : '26px', 
                      marginBottom: '10px',
                      fontWeight: 'bold'
                    }}>{item.title}</h3>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#cccccc',
                      fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                      marginBottom: '15px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginRight: '15px', marginBottom: '6px' }}>
                        <FaMapMarkerAlt style={{ marginRight: '6px', color: '#ff8c00' }} />
                        <span>{item.location}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                        <FaCalendarAlt style={{ marginRight: '6px', color: '#ff8c00' }} />
                        <span>{item.date}</span>
                      </div>
                    </div>
                    
                    <p style={{
                      color: '#aaaaaa',
                      fontSize: window.innerWidth <= 768 ? '15px' : '16px',
                      lineHeight: '1.6',
                      display: '-webkit-box',
                      WebkitLineClamp: '3',
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {item.description}
                    </p>
                  </div>
                  
                  <div style={{
                    padding: window.innerWidth <= 768 ? '0 18px 18px' : '0 25px 25px',
                    textAlign: 'right'
                  }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: '#ff8c00',
                      fontWeight: 'bold',
                      fontSize: window.innerWidth <= 768 ? '14px' : '16px'
                    }}>
                      View Featured Project
                      <svg style={{ marginLeft: '8px' }} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#ff8c00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FeaturedSection>
      )}
      
      <GallerySection>
        <div className="container">
          <div style={{
            textAlign: 'center',
            marginBottom: window.innerWidth <= 768 ? '30px' : '50px'
          }}>
            <h2 style={{
              fontSize: window.innerWidth <= 768 ? '28px' : '36px',
              fontWeight: '700',
              color: 'var(--text-color)',
              marginBottom: '15px',
              position: 'relative',
              display: 'inline-block'
            }}>
              Complete Project Gallery
              <span style={{
                content: '',
                position: 'absolute',
                bottom: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '3px',
                backgroundColor: '#ff8c00'
              }}></span>
            </h2>
            <p style={{
              fontSize: window.innerWidth <= 768 ? '16px' : '18px',
              color: '#999',
              maxWidth: '600px',
              margin: '0 auto',
              marginTop: '20px'
            }}>
              Browse through all our demolition and site clearing projects
            </p>
          </div>
          
          <div style={{
            display: 'flex',
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: window.innerWidth <= 768 ? 'stretch' : 'flex-start',
            marginBottom: '30px', 
            gap: window.innerWidth <= 768 ? '15px' : '20px',
            backgroundColor: 'rgba(10, 10, 10, 0.3)',
            padding: window.innerWidth <= 768 ? '15px' : '20px',
            borderRadius: '10px'
          }}>
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                color: '#ff8c00',
                fontWeight: '600',
                marginBottom: window.innerWidth <= 768 ? '10px' : '15px'
              }}>
                <FaFilter style={{ marginRight: '10px' }} />
                <span>Filter by Service:</span>
              </div>
              
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {categories.map((category, index) => (
                  <button 
                    key={index} 
                    style={{
                      backgroundColor: activeFilter === category ? '#ff8c00' : 'rgba(255, 255, 255, 0.05)',
                      color: activeFilter === category ? 'white' : '#cccccc',
                      border: `1px solid ${activeFilter === category ? '#ff8c00' : 'rgba(255, 255, 255, 0.1)'}`,
                      padding: window.innerWidth <= 768 ? '6px 12px' : '8px 15px',
                      borderRadius: '30px',
                      fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      outline: 'none'
                    }}
                    onClick={() => handleFilterChange(category)}
                  >
                    {category === 'all' ? 'All Projects' : category}
                  </button>
                ))}
              </div>
            </div>
            
            <div style={{
              position: 'relative',
              width: window.innerWidth <= 768 ? '100%' : '300px',
              marginTop: window.innerWidth <= 768 ? '10px' : '0'
            }}>
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{
                  width: '100%',
                  padding: window.innerWidth <= 768 ? '10px 40px 10px 15px' : '12px 45px 12px 15px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '50px',
                  color: 'white',
                  fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  WebkitAppearance: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#ff8c00';
                  e.target.style.boxShadow = '0 0 0 2px rgba(255, 140, 0, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <div style={{
                position: 'absolute',
                right: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#ff8c00',
                fontSize: '18px',
                pointerEvents: 'none'
              }}>
                <FaSearch />
              </div>
            </div>
          </div>
          
          {isLoading ? (
            <LoadingContainer>
              <Spinner />
              <p>Loading projects...</p>
            </LoadingContainer>
          ) : (
            <>
              {filteredItems.length > 0 ? (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: window.innerWidth <= 768 
                    ? 'repeat(auto-fill, minmax(280px, 1fr))' 
                    : 'repeat(3, 1fr)',
                  gap: window.innerWidth <= 768 ? '20px' : '30px',
                  marginTop: '20px',
                  maxWidth: '1200px',
                  margin: '20px auto 0'
                }}>
                  {filteredItems.map((item) => (
                    <div 
                      key={item.id}
                      style={{ 
                        cursor: 'pointer', 
                        background: 'rgba(10, 10, 10, 0.5)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 15px 30px rgba(0, 0, 0, 0.3)',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        WebkitTapHighlightColor: 'transparent',
                        minHeight: window.innerWidth <= 768 ? '350px' : '420px',
                      }}
                      onClick={() => openLightbox(item)}
                      onMouseOver={(e) => {
                        if (window.innerWidth > 768) {
                          e.currentTarget.style.transform = 'translateY(-10px)';
                          e.currentTarget.style.boxShadow = '0 20px 40px rgba(255, 140, 0, 0.4)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (window.innerWidth > 768) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 15px 30px rgba(0, 0, 0, 0.3)';
                        }
                      }}
                      onTouchStart={(e) => {
                        e.currentTarget.style.transform = 'scale(0.98)';
                        e.currentTarget.style.transition = 'transform 0.2s ease';
                      }}
                      onTouchEnd={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.transition = 'transform 0.2s ease';
                      }}
                    >
                      <div style={{ position: 'relative' }}>
                        <img 
                          src={item.image} 
                          alt={item.title} 
                          style={{ 
                            width: '100%', 
                            height: '200px', 
                            objectFit: 'cover',
                            display: 'block'
                          }} 
                        />
                        <div style={{ 
                          position: 'absolute', 
                          top: '10px', 
                          right: '10px',
                          background: '#ff8c00',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '30px',
                          fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
                        }}>
                          {item.category}
                        </div>
                      </div>
                      
                      <div style={{ padding: window.innerWidth <= 768 ? '15px' : '20px', flexGrow: 1 }}>
                        <h3 style={{ 
                          color: 'white', 
                          fontSize: window.innerWidth <= 768 ? '18px' : '22px', 
                          marginBottom: '8px',
                          fontWeight: 'bold'
                        }}>{item.title}</h3>
                        
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: '#cccccc',
                          fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                          marginBottom: '12px',
                          flexWrap: 'wrap'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginRight: '12px', marginBottom: '5px' }}>
                            <FaMapMarkerAlt style={{ marginRight: '5px', color: '#ff8c00' }} />
                            <span>{item.location}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                            <FaCalendarAlt style={{ marginRight: '5px', color: '#ff8c00' }} />
                            <span>{item.date}</span>
                          </div>
                        </div>
                        
                        <p style={{
                          color: '#aaaaaa',
                          fontSize: window.innerWidth <= 768 ? '13px' : '15px',
                          lineHeight: '1.5',
                          display: '-webkit-box',
                          WebkitLineClamp: window.innerWidth <= 768 ? '2' : '3',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {item.description}
                        </p>
                      </div>
                      
                      <div style={{
                        padding: window.innerWidth <= 768 ? '0 15px 15px' : '0 20px 20px',
                        textAlign: 'right'
                      }}>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          color: '#ff8c00',
                          fontWeight: 'bold',
                          fontSize: window.innerWidth <= 768 ? '13px' : '14px'
                        }}>
                          View Details
                          <svg style={{ marginLeft: '6px' }} width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#ff8c00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: window.innerWidth <= 768 ? '40px 20px' : '60px 20px',
                  backgroundColor: 'rgba(10, 10, 10, 0.3)',
                  borderRadius: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  marginTop: '20px',
                  maxWidth: '800px',
                  margin: '20px auto 0'
                }}>
                  <h3 style={{
                    fontSize: window.innerWidth <= 768 ? '24px' : '28px',
                    fontWeight: '700',
                    color: 'var(--text-color)',
                    marginBottom: '15px'
                  }}>No Projects Found</h3>
                  
                  <p style={{
                    fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                    color: '#999',
                    marginBottom: '25px'
                  }}>{searchTerm ? 'Try a different search term or' : 'Please try a different filter or'} view all projects.</p>
                  
                  <button 
                    onClick={() => handleFilterChange('all')}
                    style={{
                      backgroundColor: '#ff8c00',
                      color: 'white',
                      border: 'none',
                      padding: window.innerWidth <= 768 ? '10px 20px' : '12px 25px',
                      borderRadius: '30px',
                      fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
                    }}
                    onMouseOver={(e) => {
                      if (window.innerWidth > 768) {
                        e.target.style.backgroundColor = '#e67e00';
                        e.target.style.transform = 'translateY(-3px)';
                        e.target.style.boxShadow = '0 6px 15px rgba(0,0,0,0.4)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (window.innerWidth > 768) {
                        e.target.style.backgroundColor = '#ff8c00';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 10px rgba(0,0,0,0.3)';
                      }
                    }}
                  >
                    View All Projects
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </GallerySection>
      
      {/* Lightbox Modal */}
      {selectedImage && (
        <Lightbox onClick={closeLightbox}>
          <LightboxNavButton 
            left 
            onClick={goToPrevious} 
            aria-label="Previous image"
          >
            <div className="arrow-container">
              {isMobile ? (
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="3" fill="none">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              ) : (
                <FaChevronLeft />
              )}
            </div>
          </LightboxNavButton>
          
          <LightboxContent onClick={(e) => e.stopPropagation()}>
            <LightboxClose onClick={closeLightbox}>
              <FaTimes />
              <span>Close</span>
            </LightboxClose>
            
            <LightboxImage src={selectedImage.image || 'https://via.placeholder.com/800x600'} alt={selectedImage.title} />
            
            <LightboxCaption>
              <div className="lightbox-header">
                <h3>{selectedImage.title}</h3>
                <LightboxCategory>{selectedImage.category}</LightboxCategory>
              </div>
              
              <div className="lightbox-details">
                <p>{selectedImage.description}</p>
                <div className="lightbox-meta">
                  <span><strong><FaCalendarAlt /></strong> {selectedImage.date}</span>
                  <span><strong><FaMapMarkerAlt /></strong> {selectedImage.location}</span>
                </div>
              </div>
              
              <LightboxCounter>{selectedIndex + 1} of {filteredItems.length}</LightboxCounter>
            </LightboxCaption>
          </LightboxContent>
          
          <LightboxNavButton 
            right 
            onClick={goToNext} 
            aria-label="Next image"
          >
            <div className="arrow-container">
              {isMobile ? (
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="3" fill="none">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              ) : (
                <FaChevronRight />
              )}
            </div>
          </LightboxNavButton>
        </Lightbox>
      )}
    </>
  );
};

// Styled Components
const HeroSection = styled.section`
  height: 50vh;
  min-height: 400px;
  background-image: url('/images/gallery/demo1.jpg');
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  
  @media (max-width: 768px) {
    height: 40vh;
    min-height: 300px;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, rgba(0,0,0,0.85), rgba(0,0,0,0.4));
  display: flex;
  align-items: center;
  justify-content: center;
  
  h1 {
    font-size: 5rem;
    font-weight: 700;
    color: white;
    margin-bottom: 1.5rem;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 3px;
      background-color: var(--primary-color);
    }
  }
  
  p {
    font-size: 2rem;
    color: rgba(255, 255, 255, 0.9);
    max-width: 800px;
    margin: 2.5rem auto 0;
    text-shadow: 0 1px 5px rgba(0, 0, 0, 0.5);
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 3.6rem;
    }
    
    p {
      font-size: 1.8rem;
    }
  }
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 5rem;
  
  h2 {
    font-size: 3.6rem;
    font-weight: 700;
    color: var(--text-color);
    margin-bottom: 1.5rem;
    position: relative;
    display: inline-block;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 3px;
      background-color: var(--primary-color);
    }
  }
  
  p {
    font-size: 1.8rem;
    color: var(--dark-gray);
    max-width: 600px;
    margin: 0 auto;
  }
`;

const FeaturedSection = styled.section`
  padding: 8rem 0;
  background-color: var(--background-color);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.8)), url('/images/services/residential-demo.jpg');
    background-size: cover;
    background-position: center;
    opacity: 0.1;
    z-index: 0;
  }
  
  .container {
    position: relative;
    z-index: 2;
  }
  
  @media (max-width: 768px) {
    padding: 6rem 0;
  }
`;

const GallerySection = styled.section`
  padding: 8rem 0;
  background-color: var(--background-color);
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 6rem 0;
  }
`;

const FilterToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4rem;
  gap: 2rem;
  
  @media (max-width: 992px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 2rem;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  flex: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 992px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1.2rem 4.5rem 1.2rem 1.5rem;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50px;
  color: var(--text-color);
  font-size: 1.5rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(255, 140, 0, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--primary-color);
  font-size: 1.8rem;
  pointer-events: none;
`;

const FilterTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.8rem;
  color: var(--primary-color);
  font-weight: 600;
  margin-right: 2rem;
  
  svg {
    margin-right: 1rem;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const FilterButton = styled.button`
  background-color: ${props => props.active ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.active ? 'white' : 'var(--text-color)'};
  border: 1px solid ${props => props.active ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.1)'};
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
    transform: translateY(-3px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const GalleryItem = styled.div`
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  transition: transform 0.4s ease, box-shadow 0.4s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 30px rgba(0, 0, 0, 0.3);
    
    div:first-child {
      transform: scale(1.1);
    }
  }
  
  &:nth-child(3n+1) {
    transform: translateY(20px);
    
    &:hover {
      transform: translateY(10px);
    }
  }
  
  &:nth-child(3n+3) {
    transform: translateY(-20px);
    
    &:hover {
      transform: translateY(-30px);
    }
  }
  
  @media (max-width: 992px) {
    &:nth-child(3n+1), &:nth-child(3n+3) {
      transform: translateY(0);
      
      &:hover {
        transform: translateY(-10px);
      }
    }
  }
`;

const GalleryImage = styled.div`
  height: 300px;
  background-size: cover;
  background-position: center;
  transition: transform 0.5s ease;
`;

const GalleryOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 5rem 2rem 2rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0));
  transition: padding 0.3s ease;
  
  ${GalleryItem}:hover & {
    padding-bottom: 3rem;
  }
`;

const GalleryCategory = styled.span`
  display: inline-block;
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1.2rem;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
  }
`;

const GalleryTitle = styled.h3`
  font-size: 2rem;
  font-weight: 600;
  color: white;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const GalleryMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.8);
  
  span {
    margin-right: 1.5rem;
    display: flex;
    align-items: center;
    
    svg {
      margin-right: 0.8rem;
      color: var(--primary-color);
    }
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 6rem 2rem;
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: 2rem;
  
  h3 {
    font-size: 2.8rem;
    font-weight: 700;
    color: var(--text-color);
    margin-bottom: 1.5rem;
  }
  
  p {
    font-size: 1.6rem;
    color: var(--dark-gray);
    margin-bottom: 2.5rem;
  }
  
  button {
    padding: 1rem 2.5rem;
    font-size: 1.6rem;
  }
`;

const Lightbox = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const LightboxContent = styled.div`
  position: relative;
  max-width: 1000px;
  width: 100%;
  background-color: var(--background-color);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @media (max-width: 768px) {
    margin: 0 1.5rem;
    max-height: calc(100vh - 6rem);
    display: flex;
    flex-direction: column;
  }
  
  .lightbox-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    
    @media (max-width: 576px) {
      flex-direction: column;
      align-items: flex-start;
      
      h3 {
        margin-bottom: 1rem;
      }
    }
  }
  
  .lightbox-details {
    margin-bottom: 2rem;
  }
  
  .lightbox-meta {
    display: flex;
    flex-wrap: wrap;
    margin-top: 1.5rem;
    
    span {
      margin-right: 2rem;
      color: white;
      font-size: 1.4rem;
      display: flex;
      align-items: center;
      
      svg {
        margin-right: 0.8rem;
        color: var(--primary-color);
      }
      
      strong {
        color: var(--primary-color);
      }
    }
  }
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: var(--primary-color);
  color: white;
  border: none;
  width: auto;
  min-width: 4rem;
  height: 4rem;
  border-radius: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  font-size: 1.8rem;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;
  padding: 0 1.5rem;
  
  span {
    display: none;
    margin-left: 0.5rem;
    font-size: 1.4rem;
    font-weight: 600;
  }
  
  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
    
    span {
      display: inline;
    }
  }
`;

const LightboxImage = styled.img`
  width: 100%;
  display: block;
  max-height: 70vh;
  object-fit: cover;
  border-bottom: 3px solid var(--primary-color);
`;

const LightboxCaption = styled.div`
  padding: 2.5rem;
  
  h3 {
    font-size: 2.4rem;
    color: var(--text-color);
    font-weight: 700;
  }
  
  p {
    font-size: 1.6rem;
    color: white;
    line-height: 1.7;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    
    h3 {
      font-size: 2rem;
    }
  }
`;

const LightboxCategory = styled.span`
  display: inline-block;
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 5px;
  font-size: 1.4rem;
  font-weight: 600;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
`;

const LightboxCounter = styled.div`
  margin-top: 2rem;
  font-size: 1.4rem;
  color: var(--medium-gray);
  text-align: right;
`;

const LightboxNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.left ? 'left: 2rem;' : 'right: 2rem;'}
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: 2px solid white;
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1100;
  font-size: 2rem;
  transition: all 0.3s ease;
  
  .arrow-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  &:hover {
    background-color: var(--primary-color);
    border-color: var(--primary-color);
    transform: ${props => props.left ? 'translateY(-50%) translateX(-5px)' : 'translateY(-50%) translateX(5px)'};
  }
  
  @media (max-width: 768px) {
    width: 4rem;
    height: 4rem;
    ${props => props.left ? 'left: 1rem;' : 'right: 1rem;'}
    background: rgba(0, 0, 0, 0.7);
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 5rem 2rem;
  
  p {
    font-size: 1.6rem;
    color: var(--dark-gray);
    margin-top: 1rem;
  }
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default Gallery; 